import { REQUEST_METHOD } from '@/constants';
import { ADMIN_ANNOUNCEMENTS_API, ANNOUNCEMENTS_API } from '@/constants/api-endpoints';
import { apiInstance } from '@/utils';

// Super-admin vendor announcements (feature broadcast). P1 (#3425) + FULL (#3430).
// Shapes mirror the backend snake_case columns 1:1.

export type AnnouncementType = 'feature' | 'improvement' | 'maintenance' | 'critical' | 'promo';
export type AnnouncementProductLine = 'all' | 'sales' | 'marketing';
export type AnnouncementPlan = 'any' | 'free' | 'trial' | 'paid' | 'enterprise';
export type AnnouncementPlacement = 'strip' | 'toast' | 'panel';
export type AnnouncementStyle = 'subtle' | 'spotlight';
export type AnnouncementMediaType = 'none' | 'video';
export type AnnouncementVideoMode = 'inline' | 'lightbox';
export type AnnouncementStatus = 'draft' | 'scheduled' | 'live' | 'archived';

export interface Announcement {
  id: number;
  type: AnnouncementType;
  title: string;
  body: string;
  bullets: string[];
  cta_label: string | null;
  cta_link: string | null;
  media_type: AnnouncementMediaType;
  video_url: string | null;
  poster_url: string | null;
  video_mode: AnnouncementVideoMode;
  placement: AnnouncementPlacement;
  style: AnnouncementStyle;
  dismissible: boolean;
  audience_product_line: AnnouncementProductLine;
  audience_plan: AnnouncementPlan;
  status: AnnouncementStatus;
  start_at: string | null;
  end_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // present only on the /panel feed
  seen?: boolean;
}

// What the admin editor sends. All optional on update; create requires title.
export type AnnouncementInput = Partial<Omit<Announcement, 'id' | 'created_by' | 'created_at' | 'updated_at' | 'seen'>>;

export interface AnnouncementStats {
  target_reach: number;
  seen_count: number;
  dismiss_count: number;
  cta_click_count: number;
  cta_unique_clicks: number;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

// ── Admin (super-admin authoring) ────────────────────────────────────────────

export const listAdminAnnouncements = async (): Promise<Announcement[]> => {
  const res = await apiInstance<Envelope<Announcement[]>>({
    method: REQUEST_METHOD.GET,
    url: ADMIN_ANNOUNCEMENTS_API.list,
  });
  return res.data.data;
};

export const createAnnouncement = async (input: AnnouncementInput): Promise<Announcement> => {
  const res = await apiInstance<Envelope<Announcement>>({
    method: REQUEST_METHOD.POST,
    url: ADMIN_ANNOUNCEMENTS_API.create,
    data: input,
  });
  return res.data.data;
};

export const updateAnnouncement = async (id: number, input: AnnouncementInput): Promise<Announcement> => {
  const res = await apiInstance<Envelope<Announcement>>({
    method: REQUEST_METHOD.PUT,
    url: ADMIN_ANNOUNCEMENTS_API.update(id),
    data: input,
  });
  return res.data.data;
};

export const deleteAnnouncement = async (id: number): Promise<void> => {
  await apiInstance({ method: REQUEST_METHOD.DELETE, url: ADMIN_ANNOUNCEMENTS_API.remove(id) });
};

export const getAudienceCount = async (
  productLine: AnnouncementProductLine,
  plan: AnnouncementPlan,
): Promise<number> => {
  const res = await apiInstance<Envelope<{ count: number }>>({
    method: REQUEST_METHOD.GET,
    url: ADMIN_ANNOUNCEMENTS_API.audienceCount,
    params: { product_line: productLine, plan },
  });
  return res.data.data.count;
};

export const getAnnouncementStats = async (id: number): Promise<AnnouncementStats> => {
  const res = await apiInstance<Envelope<AnnouncementStats>>({
    method: REQUEST_METHOD.GET,
    url: ADMIN_ANNOUNCEMENTS_API.stats(id),
  });
  return res.data.data;
};

/** Upload a poster image; returns the public R2 URL to store in poster_url. */
export const uploadPoster = async (file: File): Promise<string> => {
  const form = new FormData();
  form.append('file', file);
  const res = await apiInstance<Envelope<{ url: string }>>({
    method: REQUEST_METHOD.POST,
    url: ADMIN_ANNOUNCEMENTS_API.poster,
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data.url;
};

// ── Vendor-facing ────────────────────────────────────────────────────────────

export const getActiveAnnouncements = async (): Promise<Announcement[]> => {
  const res = await apiInstance<Envelope<Announcement[]>>({
    method: REQUEST_METHOD.GET,
    url: ANNOUNCEMENTS_API.active,
  });
  return res.data.data;
};

export const getPanelFeed = async (): Promise<Announcement[]> => {
  const res = await apiInstance<Envelope<Announcement[]>>({
    method: REQUEST_METHOD.GET,
    url: ANNOUNCEMENTS_API.panel,
  });
  return res.data.data;
};

export const markAllAnnouncementsRead = async (): Promise<void> => {
  await apiInstance({ method: REQUEST_METHOD.POST, url: ANNOUNCEMENTS_API.markAllRead });
};

export const dismissAnnouncement = async (id: number): Promise<void> => {
  await apiInstance({ method: REQUEST_METHOD.POST, url: ANNOUNCEMENTS_API.dismiss(id) });
};

export const trackAnnouncementCtaClick = async (id: number): Promise<void> => {
  await apiInstance({ method: REQUEST_METHOD.POST, url: ANNOUNCEMENTS_API.ctaClick(id) });
};
